import { useCallback, useEffect, useMemo, useState } from 'react'
import * as React from 'react'
import TextButton from 'LuxKit/Button/TextButton'
import LineShareAltIcon from 'LuxKit/Icons/line/LineShareAltIcon'
import { trackClientEvent } from 'analytics/analytics'
import IconButton from 'LuxKit/Button/IconButton'
import type { Placement } from 'LuxKit/RichTooltip'
import RichTooltip from 'LuxKit/RichTooltip'
import { useIntl } from 'react-intl'
import { SHARE_SOCIETE_POINTS } from 'constants/share'
import { useRebrandOverlayButtonKind } from 'hooks/useRebrandOverlayButtonProps'

interface Props extends Omit<React.ComponentProps<typeof TextButton>, | 'kind' | 'children'> {
  iconOnly?: boolean
  /** When true, the share tooltip may show (once ever) after a delay */
  showToolTip?: boolean
  tooltipPlacement?: Placement
}

const SESSION_FLAG_KEY = 'le_share_tooltip_shown'

let tooltipShown = false

function hasTooltipBeenShown(): boolean {
  if (tooltipShown) return true
  if (typeof window === 'undefined') return false
  try { tooltipShown = localStorage.getItem(SESSION_FLAG_KEY) === 'true' } catch {}
  return tooltipShown
}

function markTooltipShown(): void {
  tooltipShown = true
  try { localStorage.setItem(SESSION_FLAG_KEY, 'true') } catch {}
}

function ShareWithFriendsButton(props: Props) {
  const intl = useIntl()
  const overlayButtonKind = useRebrandOverlayButtonKind()
  const { onClick, iconOnly, showToolTip, tooltipPlacement, size = 'medium', ...rest } = props

  const [isTooltipOpen, setIsTooltipOpen] = useState<boolean>(false)

  const onShareClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    onClick?.(e)
    trackClientEvent({
      subject: 'share',
      action: 'pressed',
      category: 'offer_page',
      type: 'interaction',
    })
  }, [onClick])

  const onTooltipDismiss = useCallback(() => {
    setIsTooltipOpen(false)
  }, [])

  useEffect(() => {
    if (!showToolTip || hasTooltipBeenShown()) return

    const timerId = window.setTimeout(() => {
      setIsTooltipOpen(true)
      markTooltipShown()
    }, 1000 * 10) // 10 seconds

    return () => { window.clearTimeout(timerId) }
  }, [showToolTip])

  const tooltipDescription = useMemo(() => {
    return intl.formatMessage({
      id: 'RJaNE_z1GZlzG8OZlkRc',
      defaultMessage: 'Share this offer with a friend and earn {points} Société points!',
      description: 'Tooltip description for share button when sharing an offer earns Societe points',
    }, { points: SHARE_SOCIETE_POINTS })
  }, [intl])

  const shareLabel = intl.formatMessage({
    id: 'R58guJ1O4ouA2CDXNYso',
    defaultMessage: 'Share',
    description: 'Button text to share the current offer via social media or a shareable link',
  })

  const button = iconOnly ? (
    <IconButton
      aria-label={shareLabel}
      {...rest}
      size={size}
      shape="circle"
      kind={overlayButtonKind}
      variant="dark"
      onClick={onShareClick}
    >
      <LineShareAltIcon aria-hidden="true" />
    </IconButton>
  ) : (
    <TextButton
      kind="tertiary"
      {...rest}
      size={size}
      startIcon={<LineShareAltIcon aria-hidden="true" />}
      onClick={onShareClick}
    >
      {shareLabel}
    </TextButton>
  )

  return (
    <RichTooltip
      portaled
      placement={tooltipPlacement ?? 'top-end'}
      open={isTooltipOpen}
      onDismiss={onTooltipDismiss}
      description={tooltipDescription}
    >
      {button}
    </RichTooltip>
  )
}

export default ShareWithFriendsButton
